/* RMH · Bali Pre-lander (LP3) — self-contained page behaviour. (r2)
 * Loaded once via rmh-lp3-header. Handles everything LP3 needs so the page
 * has no dependency on rmh-ce.js / rmh-adv.js:
 *   1) Scroll reveal for .ce-reveal (also covers reused rmh-adv-article)
 *   2) Reading-progress bar
 *   3) Lab-report lightbox
 *   4) Inline reaction-clip player (tap to toggle, one at a time)
 *   5) Mobile sticky CTA (shows after the hero, hides over the close/footer)
 * All effects honour prefers-reduced-motion. */
(function () {
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---- 1) Scroll reveal -------------------------------------------- */
  function initReveal() {
    var els = document.querySelectorAll('.ce-reveal');
    if (!els.length) return;
    if (reduce || !('IntersectionObserver' in window)) {
      for (var i = 0; i < els.length; i++) els[i].classList.add('is-in');
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.06 });
    for (var j = 0; j < els.length; j++) io.observe(els[j]);

    /* Fallback sweep: third-party scripts (A/B testing, chat, popups) can
       delay or swallow IntersectionObserver callbacks, leaving elements at
       opacity 0 even in the viewport. On scroll/resize (rAF-throttled),
       reveal anything already inside the same -8% line the observer uses.
       Only ever ADDS .is-in, and unhooks itself once everything is in. */
    var pending = Array.prototype.slice.call(els);
    var ticking = false;
    function sweep() {
      ticking = false;
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var line = vh * 0.92;
      for (var k = pending.length - 1; k >= 0; k--) {
        var el = pending[k];
        if (!el.classList.contains('is-in')) {
          var r = el.getBoundingClientRect();
          if (!(r.top < line && r.bottom > 0)) continue;
          el.classList.add('is-in');
          io.unobserve(el);
        }
        pending.splice(k, 1);
      }
      if (!pending.length) {
        window.removeEventListener('scroll', onSweepScroll);
        window.removeEventListener('resize', onSweepScroll);
      }
    }
    function onSweepScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(sweep);
    }
    window.addEventListener('scroll', onSweepScroll, { passive: true });
    window.addEventListener('resize', onSweepScroll, { passive: true });
    sweep();
  }

  /* ---- 2) Reading progress bar ------------------------------------- */
  function initProgress() {
    var fill = document.querySelector('[data-lp3-progress]');
    if (!fill) return;
    var ticking = false;
    function set() {
      var doc = document.documentElement;
      var max = (doc.scrollHeight - doc.clientHeight) || 1;
      var pct = Math.min(1, Math.max(0, (window.scrollY || doc.scrollTop) / max));
      fill.style.width = (pct * 100) + '%';
      ticking = false;
    }
    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(set);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    set();
  }

  /* ---- 3) Lab-report lightbox -------------------------------------- */
  function initLightbox() {
    var box = document.querySelector('[data-adv-lightbox]');
    if (!box) return;
    var triggers = document.querySelectorAll('[data-adv-lightbox-open]');
    if (!triggers.length) return;
    var lastFocus = null;
    function open(e) {
      if (e) e.preventDefault();
      lastFocus = document.activeElement;
      box.classList.add('is-open');
      box.setAttribute('aria-hidden', 'false');
      document.documentElement.style.overflow = 'hidden';
      var closeBtn = box.querySelector('[data-adv-lightbox-close]');
      if (closeBtn) closeBtn.focus();
      document.addEventListener('keydown', onKey);
    }
    function close() {
      box.classList.remove('is-open');
      box.setAttribute('aria-hidden', 'true');
      document.documentElement.style.overflow = '';
      document.removeEventListener('keydown', onKey);
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }
    function onKey(ev) { if (ev.key === 'Escape' || ev.keyCode === 27) close(); }
    for (var i = 0; i < triggers.length; i++) triggers[i].addEventListener('click', open);
    var closeEls = box.querySelectorAll('[data-adv-lightbox-close]');
    for (var j = 0; j < closeEls.length; j++) closeEls[j].addEventListener('click', close);
    box.addEventListener('click', function (ev) { if (ev.target === box) close(); });
  }

  /* ---- 4) Inline reaction-clip player ------------------------------ */
  function initClips() {
    var SEL = '.lp3-video--live';
    function play(clip, video) {
      // Pause every other live clip on the page.
      document.querySelectorAll(SEL + ' video').forEach(function (v) { if (v !== video) v.pause(); });
      var p = video.play();
      if (p && p.catch) {
        p.catch(function () { video.muted = true; var q = video.play(); if (q && q.catch) q.catch(function () {}); });
      }
    }
    document.addEventListener('click', function (e) {
      var clip = e.target.closest(SEL);
      if (!clip) return;
      var video = clip.querySelector('video');
      if (!video) return;
      e.preventDefault();
      if (video.paused) play(clip, video); else video.pause();
    }, true);
    var sync = function (e) {
      var video = e.target;
      if (!(video instanceof HTMLVideoElement)) return;
      var clip = video.closest(SEL);
      if (clip) clip.classList.toggle('is-playing', !video.paused && !video.ended);
    };
    ['play', 'playing', 'pause'].forEach(function (evt) { document.addEventListener(evt, sync, true); });
    document.addEventListener('ended', function (e) {
      var video = e.target;
      if (!(video instanceof HTMLVideoElement)) return;
      var clip = video.closest(SEL);
      if (!clip) return;
      clip.classList.remove('is-playing');
      try { video.currentTime = 0; } catch (_) {}
      video.load();
    }, true);
  }

  /* ---- 5) Mobile sticky CTA ---------------------------------------- */
  function initSticky() {
    var bar = document.querySelector('[data-lp3-sticky]');
    if (!bar || !('IntersectionObserver' in window)) return;
    var hero = document.querySelector('.rmh-lp3-hero');
    var hideZones = document.querySelectorAll('.rmh-lp3-close, .rmh-ce-footer');
    var pastHero = !hero;
    var hidingCount = 0;
    function update() {
      var show = pastHero && hidingCount === 0;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
    }
    if (hero) {
      new IntersectionObserver(function (entries) {
        pastHero = !entries[0].isIntersecting;
        update();
      }, { threshold: 0 }).observe(hero);
    }
    if (hideZones.length) {
      var zoneObs = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) { hidingCount += entry.isIntersecting ? 1 : -1; });
        if (hidingCount < 0) hidingCount = 0;
        update();
      }, { threshold: 0 });
      for (var i = 0; i < hideZones.length; i++) zoneObs.observe(hideZones[i]);
    }
    update();
  }

  function init() {
    initReveal();
    initProgress();
    initLightbox();
    initClips();
    initSticky();
  }
  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
